import React, { ReactNode, useContext } from 'react';
import { observer } from 'mobx-react';
import { useTranslation } from 'react-i18next';
import { AppContextProvider } from '../../AppLoader';
import { EnforcementCard } from '../PremiumUsers2FaEnforcement/EnforcementCard';
import { SkipEnforcementButton } from '../PremiumUsers2FaEnforcement/SkipEnforcementButton';
import { ButtonWithLoader } from '../../ButtonWithLoader/ButtonWithLoader';
import { ThemedText, ThemedTextButton } from '../../ThemedComponents';
import { HelpCircleTooltip } from './HelpCircleTooltip';
import { DownloadAppIntroBody } from './DownloadAppIntroBody';
import { DownloadAppQrBody } from './DownloadAppQrBody';
import { SelectDeviceBody } from './SelectDeviceBody';
import { BackupMethodBody } from './BackupMethodBody';
import { SuccessBody } from './SuccessBody';
import { ConfirmPendingBody } from './ConfirmPendingBody';
import { ConfirmErrorBody } from './ConfirmErrorBody';
import { UiStore as ConfirmDeviceUiStore } from './ConfirmDeviceStepUIStore';
import { ResendVerificationCode } from '../ResendVerificationCode';
import { SendNotificationState } from '../../../stores/premiumUsers2FaEnforcement/ownerApp/ownerApp';
import {
  ApprovalChallengeStatus,
  Steps,
} from '../../../stores/premiumUsers2FaEnforcement/ownerApp/types';
import { EnforcementFlow } from '../../../stores/premiumUsers2FaEnforcement/types';
import { BI_SCREEN_NAMES } from '../../../stores/constants';

interface StepConfig {
  priority?: 'primary' | 'secondary';
  title: ReactNode;
  subtitle?: ReactNode;
  primaryButton?: ReactNode;
  /** Show the shared "Use another verification method" secondary link. */
  showAnotherMethodLink?: boolean;
  showSkip?: boolean;
  showHelp?: boolean;
  /**
   * A step-specific secondary button, rendered in the secondary slot INSTEAD of the
   * shared "another method" link when `showAnotherMethodLink` is false
   * (e.g. Success's "Enable more login verification methods" side-action).
   */
  customSecondaryButton?: ReactNode;
  contentGap?: number | string;
  body: ReactNode;
}

/**
 * shell for the redesigned WixApp wizard. Renders `EnforcementCard`
 * once and wires the shared chrome (Use-another link, Skip footer, Help) a single
 * time; a per-step config supplies title/subtitle/primary/body. All actions reference
 * the same `ownerAppStore` logic the old shell uses — no logic is duplicated here.
 */
export const WixAppWizardRedesign: React.FC = observer(() => {
  const {
    rootStore: {
      postLoginStore: { premiumUsers2FaEnforcementStore: enforcementStore },
      displayStore: { preset },
    },
  } = useContext(AppContextProvider);
  const store = enforcementStore.ownerAppStore;
  const { t } = useTranslation();

  const prefix = BI_SCREEN_NAMES.WIX_APP_ENFORCEMENT;
  const skipScreenNames: Partial<Record<Steps, string>> = {
    [Steps.DownloadAppIntroStep]: `${prefix}_download_app_intro`,
    [Steps.DownloadAppQrCodeStep]: `${prefix}_download_app_qr`,
    [Steps.DeviceSelectionStep]: `${prefix}_choose_device`,
    [Steps.ConfirmationStep]: `${prefix}_confirm_device`,
  };
  const skipScreenName = skipScreenNames[store.activeStep] ?? prefix;

  // Shared chrome, wired once.
  const anotherMethodLink = (
    <ThemedTextButton
      size="small"
      underline="always"
      onClick={store.onUseAnotherVerificationMethod}
    >
      {t('wixAppEnforcement.addOwnerApp.wizard.confirm.another.method')}
    </ThemedTextButton>
  );
  const skipLink = (
    <SkipEnforcementButton size="medium" screenName={skipScreenName} />
  );
  const primary = (text: string, onClick: () => void, showLoader = false) => (
    <ButtonWithLoader
      skin={preset.buttonDesign?.skin ?? 'standard'}
      showLoader={showLoader}
      onClick={onClick}
    >
      {text}
    </ButtonWithLoader>
  );

  const isNotifying =
    store.sendNotificationState === SendNotificationState.Notifying;

  const isPending =
    store.authStatus === ApprovalChallengeStatus.PENDING_APPROVAL ||
    store.authStatus === ApprovalChallengeStatus.APPROVED;
  const noAppFlow =
    enforcementStore.enforcementFlow === EnforcementFlow.USER_HAS_NO_APP;
  const confirmUiStore = new ConfirmDeviceUiStore(store);

  const confirmationConfig: StepConfig = isPending
    ? {
        priority: 'primary',
        title: noAppFlow
          ? t('wixAppEnforcement.addOwnerApp.wizard.confirm.noApp.title')
          : t('wixAppEnforcement.addOwnerApp.wizard.header.title'),
        subtitle: noAppFlow
          ? undefined
          : t('wixAppEnforcement.addOwnerApp.wizard.header.subtitle'),
        showAnotherMethodLink: true,
        showSkip: true,
        showHelp: true,
        contentGap: 2,
        body: <ConfirmPendingBody />,
      }
    : {
        priority: 'primary',
        title: confirmUiStore.title,
        subtitle: confirmUiStore.isError ? (
          <>
            {confirmUiStore.subtitle}
            <br />
            <br />
            {t('wixAppEnforcement.addOwnerApp.wizard.confirm.error.message2')}
          </>
        ) : (
          confirmUiStore.subtitle
        ),
        primaryButton: (
          <ResendVerificationCode
            sendConfirmationCode={() => store.sendPushToSelectedDevice(true)}
            resendSuccessfullyIndication={store.resendSuccessfullyIndication}
            resendThrottleTimerValue={store.resendThrottleTimerValue}
            resendButtonTranslationKey="wixAppEnforcement.addOwnerApp.wizard.confirm.resend-link"
            resendIndicationTranslationKey="post.login.oa.2fa.code.resend.indication"
            resendButtonSize="small"
            resendButtonWeight="normal"
            showAsButton
            variant="themed"
          />
        ),
        showAnotherMethodLink: true,
        showSkip: true,
        showHelp: true,
        body: <ConfirmErrorBody />,
      };

  const stepConfigs: Partial<Record<Steps, StepConfig>> = {
    [Steps.DownloadAppIntroStep]: {
      priority: 'primary',
      title: t('wixAppEnforcement.addOwnerApp.wizard.header.title'),
      subtitle: t(
        'wixAppEnforcement.addOwnerApp.wizard.download.intro.subtitle',
      ),
      primaryButton: primary(
        t('wixAppEnforcement.addOwnerApp.wizard.download.intro.button'),
        store.nextStep,
        isNotifying,
      ),
      showAnotherMethodLink: true,
      showSkip: true,
      showHelp: true,
      body: <DownloadAppIntroBody />,
    },
    [Steps.DownloadAppQrCodeStep]: {
      priority: 'primary',
      title: t('wixAppEnforcement.addOwnerApp.wizard.download.qrcode.title'),
      subtitle: t(
        'wixAppEnforcement.addOwnerApp.wizard.download.qrcode.steps.title',
      ),
      primaryButton: primary(
        t('wixAppEnforcement.addOwnerApp.wizard.download.qrcode.button'),
        store.nextStep,
        isNotifying,
      ),
      showAnotherMethodLink: true,
      showSkip: true,
      showHelp: true,
      body: <DownloadAppQrBody />,
    },
    [Steps.DeviceSelectionStep]: {
      priority: 'primary',
      title: t('wixAppEnforcement.addOwnerApp.wizard.header.title'),
      subtitle: t('wixAppEnforcement.addOwnerApp.wizard.header.subtitle'),
      primaryButton: primary(
        t('wixAppEnforcement.addOwnerApp.wizard.select.button'),
        store.nextStep,
        isNotifying,
      ),
      showAnotherMethodLink: true,
      showSkip: true,
      showHelp: true,
      body: <SelectDeviceBody />,
    },
    [Steps.BackupMethodStep]: {
      priority: 'primary',
      title: t('wixAppEnforcement.addOwnerApp.wizard.backup.title'),
      subtitle: t('wixAppEnforcement.addOwnerApp.wizard.backup.subtitle'),
      primaryButton: primary(
        t('wixAppEnforcement.addOwnerApp.wizard.backup.cta'),
        () => store.requestAddBackupMethod(),
      ),
      body: <BackupMethodBody />,
    },
    [Steps.ConfirmationStep]: confirmationConfig,
    [Steps.SuccessfulStep]: {
      priority: 'primary',
      title: t('wixAppEnforcement.addOwnerApp.wizard.successful.title'),
      subtitle: (
        <>
          {t('wixAppEnforcement.addOwnerApp.wizard.successful.subtitle')}{' '}
          <ThemedText weight="bold" dir="ltr">
            {store.email}
          </ThemedText>
        </>
      ),
      primaryButton: primary(
        t('wixAppEnforcement.addOwnerApp.wizard.successful.cta'),
        () => store.proceedToPostAuthUrl('Add 2FA over owner app successful'),
      ),
      customSecondaryButton: (
        <ThemedTextButton
          size="small"
          underline="always"
          onClick={() => store.onEnableMoreLoginVerificationMethods()}
        >
          {t('wixAppEnforcement.addOwnerApp.wizard.successful.sideAction')}
        </ThemedTextButton>
      ),
      body: <SuccessBody />,
    },
  };

  const config = stepConfigs[store.activeStep] ?? null;

  if (!config) {
    return null;
  }

  return (
    <EnforcementCard
      priority={config.priority}
      title={config.title}
      subtitle={config.subtitle}
      contentGap={config.contentGap}
      topRightAction={config.showHelp ? <HelpCircleTooltip /> : undefined}
      primaryButton={config.primaryButton}
      secondaryButton={
        config.showAnotherMethodLink
          ? anotherMethodLink
          : config.customSecondaryButton
      }
      footer={config.showSkip ? skipLink : undefined}
    >
      {config.body}
    </EnforcementCard>
  );
});
